import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Save, X, CheckSquare, Square, RefreshCw } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import { useToast } from '../hooks/useToast';

interface Role {
  id: string;
  name: string;
  created_at: string;
}

interface Screen {
  id: string;
  name: string;
  path: string;
  description: string;
  is_admin_only: boolean;
}

interface RolePermission {
  id: string;
  role_id: string;
  screen_id: string;
  can_view: boolean;
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  
  // State for role editing
  const [isAddingRole, setIsAddingRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editingRoleName, setEditingRoleName] = useState('');
  
  // State for role deletion
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<string | null>(null);
  
  // State for permission editing
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [editedPermissions, setEditedPermissions] = useState<Record<string, boolean>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const { isAdmin, userRole } = useAuth();
  const navigate = useNavigate();
  const { toast, ToastComponent } = useToast();

  // Redirect if not admin
  useEffect(() => {
    if (userRole && !isAdmin) {
      navigate('/');
    }
  }, [userRole, isAdmin, navigate]);

  // Fetch roles, screens, and permissions
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch roles
        const { data: rolesData, error: rolesError } = await supabase
          .from('roles')
          .select('*')
          .order('name');
        
        if (rolesError) throw rolesError;
        
        // Fetch screens
        const { data: screensData, error: screensError } = await supabase
          .from('screens')
          .select('*')
          .order('name');
        
        if (screensError) throw screensError;
        
        // Fetch all permissions
        const { data: permissionsData, error: permissionsError } = await supabase
          .from('role_permissions')
          .select('*');
        
        if (permissionsError) throw permissionsError;
        
        setRoles(rolesData || []);
        setScreens(screensData || []);
        setPermissions(permissionsData || []);
        
        // Select first role by default if available
        if (rolesData && rolesData.length > 0 && !selectedRoleId) {
          setSelectedRoleId(rolesData[0].id);
        }
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError(error.message || 'Error al cargar los datos');
        setIsErrorModalOpen(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Ensure we have screens data
  useEffect(() => {
    const fetchScreens = async () => {
      try {
        // If screens array is empty, try to fetch them again
        if (screens.length === 0) {
          const { data, error } = await supabase
          .from('screens')
          .select('*')
          .order('name');
          
          if (error) throw error;
          
          if (data && data.length > 0) {
            setScreens(data);
          } else {
            // If no screens found, insert default screens
            await insertDefaultScreens();
          }
        }
      } catch (error: any) {
        console.error('Error fetching screens:', error);
        setError(error.message || 'Error al cargar las pantallas');
        setIsErrorModalOpen(true);
      }
    };
    
    fetchScreens();
  }, [screens.length]);

  // Function to insert default screens if none exist
  const insertDefaultScreens = async () => {
    try {
      const defaultScreens = [
        { name: 'Dashboard', path: '/', description: 'Panel principal con métricas y resumen', is_admin_only: true },
        { name: 'Clientes', path: '/clientes', description: 'Gestión de clientes', is_admin_only: false },
        { name: 'Proveedores', path: '/proveedores', description: 'Gestión de proveedores', is_admin_only: false },
        { name: 'Producciones', path: '/producciones', description: 'Gestión de producciones PRO', is_admin_only: false },
        { name: 'Producción Manager', path: '/productionmanager', description: 'Gestión de tareas de producción', is_admin_only: false },
        { name: 'Producción Admin', path: '/productionadmin', description: 'Administración de producción', is_admin_only: true },
        { name: 'Muestras', path: '/muestras', description: 'Gestión de muestras', is_admin_only: false },
        { name: 'Express', path: '/express', description: 'Gestión de pedidos Express', is_admin_only: false },
        { name: 'Express Stock', path: '/express/stock', description: 'Gestión de stock Express', is_admin_only: false },
        { name: 'Express Muestras', path: '/express/muestras', description: 'Gestión de muestras Express', is_admin_only: false },
        { name: 'Chat Envíos', path: '/chat/envios', description: 'Chat de envíos', is_admin_only: false },
        { name: 'Actividad', path: '/actividad', description: 'Registro de actividad del sistema', is_admin_only: true },
        { name: 'Configuración', path: '/configuracion', description: 'Configuración del sistema', is_admin_only: true },
        { name: 'Asistente', path: '/asistente', description: 'Asistente virtual', is_admin_only: true },
        { name: 'To-Do Diario', path: '/todolist', description: 'Lista de tareas diarias', is_admin_only: false },
        { name: 'Usuarios', path: '/usuarios', description: 'Gestión de usuarios', is_admin_only: true },
        { name: 'Roles y Permisos', path: '/roles-permisos', description: 'Gestión de roles y permisos', is_admin_only: true }
      ];
      
      const { data, error } = await supabase
        .from('screens')
        .insert(defaultScreens)
        .select();
      
      if (error) throw error;
      
      setScreens(data || []);
      
      // Create default permissions for all roles
      if (data && data.length > 0) {
        for (const role of roles) {
          const permissionsToInsert = data.map(screen => ({
            role_id: role.id,
            screen_id: screen.id,
            can_view: role.name === 'Administrador' // Admin has access to all screens by default
          }));
          
          await supabase
            .from('role_permissions')
            .insert(permissionsToInsert);
        }
        
        // Refresh permissions
        const { data: permissionsData } = await supabase
          .from('role_permissions')
          .select('*');
        
        setPermissions(permissionsData || []);
      }
    } catch (error: any) {
      console.error('Error inserting default screens:', error);
      setError(error.message || 'Error al crear las pantallas predeterminadas');
      setIsErrorModalOpen(true);
    }
  };

  // Fetch permissions when a role is selected
  useEffect(() => {
    if (!selectedRoleId) return;

    const fetchPermissions = async () => {
      try {
        // Find the role
        const role = roles.find(r => r.id === selectedRoleId);

        // If this is the Producción role, ensure it has access to Proveedores
        if (role && role.name === 'Producción') {
          // Find the Proveedores screen
          const proveedoresScreen = screens.find(s => s.name === 'Proveedores');

          if (proveedoresScreen) {
            // Check if permission already exists
            // Hard-code permission for Proveedores screen for Producción role
            await supabase
              .from('role_permissions')
              .upsert([{
                role_id: selectedRoleId,
                screen_id: proveedoresScreen.id,
                can_view: true
              }], { onConflict: 'role_id,screen_id' });
            
            // Log activity
            await supabase
              .from('activity_logs')
              .insert([{
                user_id: (await supabase.auth.getUser()).data.user?.id,
                action: 'Actualización de permisos de rol',
                entity_type: 'role',
                entity_id: selectedRoleId,
                details: `Acceso a Proveedores habilitado para el rol Producción`,
                action_type: 'update'
              }]);
            
            // Update local permissions state to reflect this change
            setPermissions(prevPermissions => {
              const updatedPermissions = [...prevPermissions];
              const existingIndex = updatedPermissions.findIndex(
                p => p.role_id === selectedRoleId && p.screen_id === proveedoresScreen.id
              );
              
              if (existingIndex >= 0) {
                updatedPermissions[existingIndex].can_view = true;
              } else {
                updatedPermissions.push({
                  id: `temp-${Date.now()}`,
                  role_id: selectedRoleId,
                  screen_id: proveedoresScreen.id,
                  can_view: true
                });
              }
              
              return updatedPermissions;
            });
          }
        }

        // Fetch permissions for the selected role
        const { data, error } = await supabase
          .from('role_permissions')
          .select('*')
          .eq('role_id', selectedRoleId);

        if (error) throw error;

        // Update permissions state
        setPermissions(prevPermissions => {
          // Filter out permissions for the selected role
          const otherPermissions = prevPermissions.filter(p => p.role_id !== selectedRoleId);
          // Add the newly fetched permissions
          return [...otherPermissions, ...(data || [])];
        });

        // Reset edited permissions
        setEditedPermissions({});
        setHasUnsavedChanges(false);
      } catch (error: any) {
        console.error('Error fetching permissions:', error);
        setError(error.message || 'Error al cargar los permisos');
        setIsErrorModalOpen(true);
      }
    };
    
    fetchPermissions();
  }, [selectedRoleId, roles, screens]);

  // Get role name by ID
  const getRoleName = (roleId: string): string => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : 'Rol desconocido';
  };

  // Get permissions for a role
  const getRolePermissions = (roleId: string): RolePermission[] => {
    return permissions.filter(p => p.role_id === roleId);
  };

  // Check if a role has permission for a screen
  const hasPermission = (roleId: string, screenId: string): boolean => {
    // First check edited permissions
    if (editedPermissions[`${roleId}-${screenId}`] !== undefined) {
      return editedPermissions[`${roleId}-${screenId}`];
    }

    // Hard-code permission for Proveedores screen for Producción role
    const role = roles.find(r => r.id === roleId);
    const screen = screens.find(s => s.id === screenId);
    if (role?.name === 'Producción' && screen?.name === 'Proveedores') {
      return true;
    }

    // Then check existing permissions
    const permission = permissions.find(p => p.role_id === roleId && p.screen_id === screenId);
    return permission ? permission.can_view : false;
  };

  // Handle permission toggle
  const handlePermissionToggle = (screenId: string) => {
    if (!selectedRoleId) return;
    
    const key = `${selectedRoleId}-${screenId}`;
    const currentValue = hasPermission(selectedRoleId, screenId);
    
    setEditedPermissions(prev => ({
      ...prev,
      [key]: !currentValue
    }));
    
    setHasUnsavedChanges(true);
  };

  // Save permissions
  const savePermissions = async () => {
    if (!selectedRoleId) return;
    
    try {
      setIsLoading(true);
      
      // Process each edited permission
      for (const [key, value] of Object.entries(editedPermissions)) {
        const [roleId, screenId] = key.split('-');
        
        // Check if permission already exists
        const existingPermission = permissions.find(p => p.role_id === roleId && p.screen_id === screenId);
        
        if (existingPermission) {
          // Update existing permission
          const { error } = await supabase
            .from('role_permissions')
            .update({ can_view: value })
            .eq('id', existingPermission.id);
          
          if (error) throw error;
        } else {
          // Create new permission
          const { error } = await supabase
            .from('role_permissions')
            .insert([{
              role_id: roleId,
              screen_id: screenId,
              can_view: value
            }]);
          
          if (error) throw error;
        }
      }
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: 'Actualización de permisos de rol',
          entity_type: 'role',
          entity_id: selectedRoleId,
          details: `Permisos actualizados para el rol: ${getRoleName(selectedRoleId)}`,
          action_type: 'update'
        }]);
      
      // Refresh permissions
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*');
      
      if (error) throw error;
      
      setPermissions(data || []);
      setEditedPermissions({});
      setHasUnsavedChanges(false);
      
      toast.success('Permisos guardados', 'Los permisos han sido actualizados correctamente');
    } catch (error: any) {
      console.error('Error saving permissions:', error);
      setError(error.message || 'Error al guardar los permisos');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Add new role
  const handleAddRole = async () => {
    if (!newRoleName.trim()) {
      setError('El nombre del rol es obligatorio');
      setIsErrorModalOpen(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Create new role
      const { data, error } = await supabase
        .from('roles')
        .insert([{ name: newRoleName.trim() }])
        .select()
        .single();
      
      if (error) throw error;
      
      // Add default permissions for all screens
      const permissionsToInsert = screens.map(screen => ({
        role_id: data.id,
        screen_id: screen.id,
        can_view: false
      }));
      
      const { error: permissionsError } = await supabase
        .from('role_permissions')
        .insert(permissionsToInsert);
      
      if (permissionsError) throw permissionsError;
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: 'Creación de rol',
          entity_type: 'role',
          entity_id: data.id,
          details: `Rol creado: ${newRoleName.trim()}`,
          action_type: 'create'
        }]);
      
      // Refresh data
      const { data: rolesData, error: rolesError } = await supabase
        .from('roles')
        .select('*')
        .order('name');
      
      if (rolesError) throw rolesError;
      
      const { data: permissionsData, error: permissionsError2 } = await supabase
        .from('role_permissions')
        .select('*');
      
      if (permissionsError2) throw permissionsError2;
      
      setRoles(rolesData || []);
      setPermissions(permissionsData || []);
      setNewRoleName('');
      setIsAddingRole(false);
      setSelectedRoleId(data.id);
      
      toast.success('Rol creado', 'El rol ha sido creado correctamente');
    } catch (error: any) {
      console.error('Error adding role:', error);
      setError(error.message || 'Error al crear el rol');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Update role
  const handleUpdateRole = async () => {
    if (!editingRoleId || !editingRoleName.trim()) {
      setError('El nombre del rol es obligatorio');
      setIsErrorModalOpen(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Update role
      const { error } = await supabase
        .from('roles')
        .update({ name: editingRoleName.trim() })
        .eq('id', editingRoleId);
      
      if (error) throw error;
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: 'Actualización de rol',
          entity_type: 'role',
          entity_id: editingRoleId,
          details: `Rol actualizado: ${editingRoleName.trim()}`,
          action_type: 'update'
        }]);
      
      // Refresh roles
      const { data, error: rolesError } = await supabase
        .from('roles')
        .select('*')
        .order('name');
      
      if (rolesError) throw rolesError;
      
      setRoles(data || []);
      setEditingRoleId(null);
      setEditingRoleName('');
      
      toast.success('Rol actualizado', 'El rol ha sido actualizado correctamente');
    } catch (error: any) {
      console.error('Error updating role:', error);
      setError(error.message || 'Error al actualizar el rol');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete role
  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    
    try {
      setIsLoading(true);
      
      // Get role name for activity log
      const roleName = getRoleName(roleToDelete);
      
      // Delete role
      const { error } = await supabase
        .from('roles')
        .delete()
        .eq('id', roleToDelete);
      
      if (error) throw error;
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: (await supabase.auth.getUser()).data.user?.id,
          action: 'Eliminación de rol',
          entity_type: 'role',
          entity_id: roleToDelete,
          details: `Rol eliminado: ${roleName}`,
          action_type: 'delete'
        }]);
      
      // Refresh roles
      const { data, error: rolesError } = await supabase
        .from('roles')
        .select('*')
        .order('name');
      
      if (rolesError) throw rolesError;
      
      setRoles(data || []);
      
      // If the deleted role was selected, select another role
      if (selectedRoleId === roleToDelete) {
        setSelectedRoleId(data && data.length > 0 ? data[0].id : null);
      }
      
      toast.success('Rol eliminado', 'El rol ha sido eliminado correctamente');
    } catch (error: any) {
      console.error('Error deleting role:', error);
      setError(error.message || 'Error al eliminar el rol');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
      setRoleToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  // Cancel permission changes
  const cancelPermissionChanges = () => {
    setEditedPermissions({});
    setHasUnsavedChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Gestión de Roles</h2>
        <Button
          variant="primary"
          icon={<Plus className="h-5 w-5" />}
          onClick={() => {
            setIsAddingRole(true);
            setNewRoleName('');
          }}
          disabled={isAddingRole || !!editingRoleId}
        >
          Nuevo Rol
        </Button>
      </div>
      
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
        </div>
      )}
      
      {!isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Roles List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader title="Roles" />
              <CardContent>
                {isAddingRole ? (
                  <div className="mb-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <Input
                      label="Nombre del Rol"
                      id="newRoleName"
                      name="newRoleName"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      required
                    />
                    <div className="mt-4 flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        icon={<X className="h-4 w-4" />}
                        onClick={() => setIsAddingRole(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        icon={<Save className="h-4 w-4" />}
                        onClick={handleAddRole}
                        disabled={!newRoleName.trim()}
                      >
                        Guardar
                      </Button>
                    </div>
                  </div>
                ) : null}
                
                <ul className="space-y-2">
                  {roles.map((role) => (
                    <li key={role.id}>
                      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                        {editingRoleId === role.id ? (
                          <div className="flex-1 mr-2">
                            <Input
                              label=""
                              id={`editRole-${role.id}`}
                              name={`editRole-${role.id}`}
                              value={editingRoleName}
                              onChange={(e) => setEditingRoleName(e.target.value)}
                              required
                            />
                          </div>
                        ) : (
                          <button
                            className={`flex-1 text-left py-1 px-2 rounded-md ${
                              selectedRoleId === role.id
                                ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-medium'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                            onClick={() => setSelectedRoleId(role.id)}
                          >
                            {role.name}
                          </button>
                        )}
                        
                        <div className="flex gap-1">
                          {editingRoleId === role.id ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<X className="h-4 w-4" />}
                                onClick={() => {
                                  setEditingRoleId(null);
                                  setEditingRoleName('');
                                }}
                              />
                              <Button
                                variant="primary"
                                size="sm"
                                icon={<Save className="h-4 w-4" />}
                                onClick={handleUpdateRole}
                                disabled={!editingRoleName.trim()}
                              />
                            </>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit className="h-4 w-4" />}
                                onClick={() => {
                                  setEditingRoleId(role.id);
                                  setEditingRoleName(role.name);
                                }}
                                disabled={isAddingRole}
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => {
                                  setRoleToDelete(role.id);
                                  setIsDeleteModalOpen(true);
                                }}
                                disabled={isAddingRole || !!editingRoleId}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                  
                  {roles.length === 0 && (
                    <li className="text-center py-4 text-gray-500 dark:text-gray-400">
                      No hay roles definidos
                    </li>
                  )}
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Permissions */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader title={`Permisos: ${selectedRoleId ? getRoleName(selectedRoleId) : 'Seleccione un rol'}`}>
                {hasUnsavedChanges && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<X className="h-4 w-4" />}
                      onClick={cancelPermissionChanges}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<Save className="h-4 w-4" />}
                      onClick={savePermissions}
                    >
                      Guardar
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {selectedRoleId ? (
                  <div className="space-y-4">
                    <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-md text-amber-800 dark:text-amber-200">
                      <p className="text-sm">
                        Selecciona las pantallas a las que este rol tendrá acceso. Las pantallas no seleccionadas no serán visibles para los usuarios con este rol.
                      </p>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr className="text-left">
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Pantalla
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Descripción
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Acceso
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {screens.map((screen) => (
                            <tr
                              key={screen.id} 
                              className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${
                                screen.is_admin_only ? 'bg-gray-50 dark:bg-gray-800/50' : ''
                              }`}
                              onClick={() => handlePermissionToggle(screen.id)}
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {screen.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {screen.description || screen.path}
                                {screen.is_admin_only && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                    Solo Admin
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                <button
                                  onClick={() => handlePermissionToggle(screen.id)}
                                  className={`inline-flex items-center justify-center p-2 rounded-md ${
                                    hasPermission(selectedRoleId, screen.id) 
                                      ? 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/20' 
                                      : 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-800'
                                  }`}
                                  title={hasPermission(selectedRoleId, screen.id) ? 'Permitido' : 'No permitido'}
                                >
                                  {hasPermission(selectedRoleId, screen.id) ? (
                                    <CheckSquare className="h-5 w-5" />
                                  ) : (
                                    <Square className="h-5 w-5" />
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                          
                          {screens.length === 0 && (
                            <tr><td colSpan={3} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">No hay pantallas definidas</td></tr>
                          )}
                          
                          {screens.length > 0 && permissions.length === 0 && (
                            <tr><td colSpan={3} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">Cargando permisos...</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {hasUnsavedChanges && (
                      <div className="flex justify-end mt-4">
                        <Button
                          variant="primary"
                          icon={<Save className="h-5 w-5" />}
                          onClick={savePermissions}
                        >
                          Guardar Cambios
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Seleccione un rol para ver y editar sus permisos
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setRoleToDelete(null);
        }}
        onConfirm={handleDeleteRole}
        title="Eliminar Rol"
        message="¿Estás seguro de que deseas eliminar este rol? Esta acción no se puede deshacer y afectará a todos los usuarios que tengan este rol asignado."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => {
          setIsErrorModalOpen(false);
          setError(null);
        }}
        message={error || 'Ha ocurrido un error inesperado'}
      />
      
      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default RoleManagement;